// Minimal XML parser for untrusted SVG. It has no DTD or entity support on purpose, so XXE and
// entity-expansion attacks are impossible by construction (no DOMParser, nothing touches the DOM).

export interface XmlElement {
  readonly name: string;
  readonly attrs: Readonly<Record<string, string>>;
  readonly children: readonly XmlNode[];
}

export interface XmlText {
  readonly text: string;
}

export type XmlNode = XmlElement | XmlText;

export const isXmlElement = (node: XmlNode): node is XmlElement => (node as XmlElement).name !== undefined;

export class SvgImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SvgImportError";
  }
}

export interface XmlLimits {
  readonly maxElements: number;
  readonly maxDepth: number;
}

interface MutableElement {
  name: string;
  attrs: Record<string, string>;
  children: XmlNode[];
}

const PREDEFINED_ENTITIES: Readonly<Record<string, string>> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

const MAX_CODE_POINT = 0x10ffff;

export const decodeEntities = (raw: string): string =>
  raw.replace(/&(#x[0-9a-fA-F]+|#[0-9]+|[a-zA-Z]+);/g, (match, body: string) => {
    if (body[0] === "#") {
      const code = body[1] === "x" || body[1] === "X" ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code <= MAX_CODE_POINT ? String.fromCodePoint(code) : "";
    }
    // Unknown named entities would need a DTD; drop them rather than expand anything.
    return PREDEFINED_ENTITIES[body] ?? "";
  });

const NAME_RE = /[A-Za-z_:][-A-Za-z0-9_:.]*/y;
const ATTR_RE = /\s*([A-Za-z_:][-A-Za-z0-9_:.]*)\s*(?:=\s*("([^"]*)"|'([^']*)'))?/y;

const localName = (qualified: string): string => {
  const colon = qualified.indexOf(":");
  // Keep the xlink prefix so href lookups can tell xlink:href and href apart if needed.
  if (colon === -1 || qualified.startsWith("xlink:") || qualified.startsWith("xml:")) return qualified;
  return qualified.slice(colon + 1);
};

const skipPast = (src: string, from: number, terminator: string, what: string): number => {
  const end = src.indexOf(terminator, from);
  if (end === -1) throw new SvgImportError(`Malformed SVG: unterminated ${what}`);
  return end + terminator.length;
};

const skipDoctype = (src: string, from: number): number => {
  const close = src.indexOf(">", from);
  const bracket = src.indexOf("[", from);
  if (bracket !== -1 && (close === -1 || bracket < close)) {
    throw new SvgImportError("SVG files with an internal DTD or entity declarations are not allowed");
  }
  if (close === -1) throw new SvgImportError("Malformed SVG: unterminated DOCTYPE");
  return close + 1;
};

const parseAttributes = (src: string, from: number): { attrs: Record<string, string>; end: number } => {
  const attrs: Record<string, string> = Object.create(null);
  let pos = from;
  for (;;) {
    ATTR_RE.lastIndex = pos;
    const match = ATTR_RE.exec(src);
    if (!match || match[0].length === 0) break;
    const value = match[3] ?? match[4] ?? "";
    attrs[localName(match[1])] = decodeEntities(value);
    pos = ATTR_RE.lastIndex;
  }
  while (pos < src.length && /\s/.test(src[pos])) pos += 1;
  return { attrs, end: pos };
};

/** Parses an XML string into a tree. Throws SvgImportError on malformed or disallowed input. */
export const parseXml = (src: string, limits: XmlLimits): XmlElement => {
  const root: MutableElement = { name: "#document", attrs: {}, children: [] };
  const stack: MutableElement[] = [root];
  let elementCount = 0;
  let pos = 0;

  while (pos < src.length) {
    const lt = src.indexOf("<", pos);
    const textEnd = lt === -1 ? src.length : lt;
    if (textEnd > pos) {
      stack[stack.length - 1].children.push({ text: decodeEntities(src.slice(pos, textEnd)) });
    }
    if (lt === -1) break;
    pos = lt;

    if (src.startsWith("<!--", pos)) {
      pos = skipPast(src, pos + 4, "-->", "comment");
    } else if (src.startsWith("<![CDATA[", pos)) {
      const end = src.indexOf("]]>", pos + 9);
      if (end === -1) throw new SvgImportError("Malformed SVG: unterminated CDATA");
      stack[stack.length - 1].children.push({ text: src.slice(pos + 9, end) });
      pos = end + 3;
    } else if (src.startsWith("<?", pos)) {
      pos = skipPast(src, pos + 2, "?>", "processing instruction");
    } else if (/^<!doctype/i.test(src.slice(pos, pos + 9))) {
      pos = skipDoctype(src, pos);
    } else if (src.startsWith("</", pos)) {
      const end = skipPast(src, pos + 2, ">", "closing tag");
      const name = localName(src.slice(pos + 2, end - 1).trim());
      // Lenient close: pop to the nearest matching open element; ignore strays.
      const index = stack.map((el) => el.name).lastIndexOf(name);
      if (index > 0) stack.length = index;
      pos = end;
    } else {
      NAME_RE.lastIndex = pos + 1;
      const nameMatch = NAME_RE.exec(src);
      if (!nameMatch) throw new SvgImportError("Malformed SVG: invalid tag");
      const { attrs, end } = parseAttributes(src, NAME_RE.lastIndex);
      const selfClosing = src.startsWith("/>", end);
      if (!selfClosing && src[end] !== ">") throw new SvgImportError("Malformed SVG: invalid tag");
      elementCount += 1;
      if (elementCount > limits.maxElements) {
        throw new SvgImportError(`SVG is too complex (more than ${limits.maxElements} elements)`);
      }
      const element: MutableElement = { name: localName(nameMatch[0]), attrs, children: [] };
      stack[stack.length - 1].children.push(element);
      if (!selfClosing) {
        if (stack.length > limits.maxDepth) throw new SvgImportError("SVG is nested too deeply");
        stack.push(element);
      }
      pos = end + (selfClosing ? 2 : 1);
    }
  }

  const svg = root.children.find((child): child is XmlElement => isXmlElement(child) && child.name === "svg");
  if (!svg) throw new SvgImportError("The file is not an SVG (no <svg> root element)");
  return svg;
};
